import { useState, useEffect } from 'react';
import stripeService from '../../services/stripeService';

// Import Material UI components
import {
  Box, Typography, Paper, Button, Card, CardContent, CardActions,
  Divider, Alert, CircularProgress, Grid, TextField, InputAdornment,
  List, ListItem, ListItemIcon, ListItemText, IconButton, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
  Switch, FormControlLabel, Chip, Tooltip, Menu, MenuItem, Container
} from '@mui/material';

// Import Material UI icons
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  AssessmentOutlined as AssessmentIcon,
  MoreVert as MoreVertIcon,
  StarOutline as StarIcon,
  Star as StarFilledIcon,
  AddCircleOutline as AddCircleIcon,
  RemoveCircleOutline as RemoveCircleIcon
} from '@mui/icons-material';

// Format currency
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

// Helper function to detect Stripe API key errors
const isStripeApiKeyError = (error) => {
  const errorMsg = error?.response?.data?.message || error?.message || '';
  return errorMsg.toLowerCase().includes('api key') || 
         errorMsg.toLowerCase().includes('invalid key') ||
         errorMsg.toLowerCase().includes('authentication failed') ||
         errorMsg.toLowerCase().includes('invalid auth');
};

const SuperAdminPlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [isApiKeyError, setIsApiKeyError] = useState(false);
  
  // New plan form state
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price: '',
    interval: 'month',
    features: [''],
    isRecommended: false,
    active: true,
    planId: 'basic'
  });
  
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsApiKeyError(false);
        
        console.log('Initiating real API call to fetch subscription plans...');
        
        // Fetch data from the real API with improved error handling
        const response = await stripeService.getPlatformPlans();
        
        console.log('API response received:', response);
        
        // Special error handling for not implemented endpoints
        if (response.notImplemented) {
          setError(`This endpoint is not fully implemented on your backend server. Using default plan data instead.`);
          setPlans(response.data || []);
          setLoading(false);
          return;
        }
        
        // Authentication errors
        if (response.authError) {
          setError(`Authentication error. Please check your login credentials and try again.`);
          setIsApiKeyError(true);
          setPlans(response.data || []);
          setLoading(false);
          return;
        }
        
        // Success handling - check if response has data array
        if (response.success === true && Array.isArray(response.data)) {
          console.log('Raw plans from API:', response.data.length, 'plans');
          
          // Check if we have recently deleted a plan
          const lastDeletedPlanId = localStorage.getItem('lastDeletedPlanId');
          const lastDeletedTimestamp = localStorage.getItem('lastDeletedTimestamp');
          const now = Date.now();
          
          // If we deleted a plan in the last 5 minutes, filter it out of the results
          let filteredPlans = [...response.data];
          if (lastDeletedPlanId && lastDeletedTimestamp && 
              (now - parseInt(lastDeletedTimestamp)) < 5 * 60 * 1000) {
            
            console.log(`Filtering out recently deleted plan: ${lastDeletedPlanId}`);
            filteredPlans = filteredPlans.filter(plan => plan.id !== lastDeletedPlanId);
            
            // If the plan count changed, log it
            if (filteredPlans.length !== response.data.length) {
              console.log(`Filtered out ${response.data.length - filteredPlans.length} deleted plans`);
            }
          }
          
          // Set the filtered plans
          console.log('Setting plans after filtering:', filteredPlans.length);
          setPlans(filteredPlans);
          
          // Show source message if using default plans
          if (response.source === 'default') {
            setError("Using default plan data - couldn't connect to real API");
          }
        } else {
          // If no success flag or not an array data, treat as error
          console.error('API returned unexpected data format:', response);
          setError("API returned unexpected data format. Using default plans.");
          
          // Still use any provided data if available
          if (response.data && Array.isArray(response.data)) {
            setPlans(response.data);
          } else {
            setPlans([]);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Failed to load plans from API:", err);
        
        if (isStripeApiKeyError(err)) {
          setIsApiKeyError(true);
          setError("Invalid Stripe API Key detected. Please check your server configuration.");
        } else {
          setError(
            err.response?.data?.message || 
            err.message || 
            "Failed to load subscription plans. Please check the API connection."
          );
        }
        
        // Load default plans as fallback
        try {
          const defaultPlans = stripeService.getDefaultPlans();
          setPlans(defaultPlans || []);
        } catch (defaultError) {
          setPlans([]);
        }
        
        setLoading(false);
      }
    };
    
    loadPlans();
  }, []);
  
  // Add a refresh button handler
  const handleRefreshPlans = async () => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        setIsApiKeyError(false);
        
        console.log('Initiating real API call to fetch subscription plans...');
        
        // Fetch data from the real API with improved error handling
        const response = await stripeService.getPlatformPlans();
        
        console.log('API response received:', response);
        
        // Special error handling for not implemented endpoints
        if (response.notImplemented) {
          setError(`This endpoint is not fully implemented on your backend server. Using default plan data instead.`);
          setPlans(response.data || []);
          setLoading(false);
          return;
        }
        
        // Authentication errors
        if (response.authError) {
          setError(`Authentication error. Please check your login credentials and try again.`);
          setIsApiKeyError(true);
          setPlans(response.data || []);
          setLoading(false);
          return;
        }
        
        // Success handling - check if response has data array
        if (response.success === true && Array.isArray(response.data)) {
          console.log('Raw plans from API:', response.data.length, 'plans');
          
          // Check if we have recently deleted a plan
          const lastDeletedPlanId = localStorage.getItem('lastDeletedPlanId');
          const lastDeletedTimestamp = localStorage.getItem('lastDeletedTimestamp');
          const now = Date.now();
          
          // If we deleted a plan in the last 5 minutes, filter it out of the results
          let filteredPlans = [...response.data];
          if (lastDeletedPlanId && lastDeletedTimestamp && 
              (now - parseInt(lastDeletedTimestamp)) < 5 * 60 * 1000) {
            
            console.log(`Filtering out recently deleted plan: ${lastDeletedPlanId}`);
            filteredPlans = filteredPlans.filter(plan => plan.id !== lastDeletedPlanId);
            
            // If the plan count changed, log it
            if (filteredPlans.length !== response.data.length) {
              console.log(`Filtered out ${response.data.length - filteredPlans.length} deleted plans`);
            }
          }
          
          // Set the filtered plans
          console.log('Setting plans after filtering:', filteredPlans.length);
          setPlans(filteredPlans);
          
          // Show source message if using default plans
          if (response.source === 'default') {
            setError("Using default plan data - couldn't connect to real API");
          }
        } else {
          // If no success flag or not an array data, treat as error
          console.error('API returned unexpected data format:', response);
          setError("API returned unexpected data format. Using default plans.");
          
          // Still use any provided data if available
          if (response.data && Array.isArray(response.data)) {
            setPlans(response.data);
          } else {
            setPlans([]);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Failed to load plans from API:", err);
        
        if (isStripeApiKeyError(err)) {
          setIsApiKeyError(true);
          setError("Invalid Stripe API Key detected. Please check your server configuration.");
        } else {
          setError(
            err.response?.data?.message || 
            err.message || 
            "Failed to load subscription plans. Please check the API connection."
          );
        }
        
        // Load default plans as fallback
        try {
          const defaultPlans = stripeService.getDefaultPlans();
          setPlans(defaultPlans || []);
        } catch (defaultError) {
          setPlans([]);
        }
        
        setLoading(false);
      }
    };
    
    loadPlans();
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      // Edit mode
      setEditMode(true);
      setCurrentPlan(plan);
      setPlanForm({
        name: plan.name || '',
        description: plan.description || '',
        price: plan.price || '',
        interval: plan.interval || 'month',
        features: plan.features || [''],
        isRecommended: plan.recommended || false,
        active: plan.active !== false,
        planId: plan.planId || 'basic'
      });
    } else {
      // Create mode
      setEditMode(false);
      setCurrentPlan(null);
      setPlanForm({
        name: '',
        description: '',
        price: '',
        interval: 'month',
        features: [''],
        isRecommended: false,
        active: true,
        planId: 'basic'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPlanForm({
      name: '',
      description: '',
      price: '',
      interval: 'month',
      features: [''],
      isRecommended: false,
      active: true,
      planId: 'basic'
    });
    setEditMode(false);
    setCurrentPlan(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'isRecommended' || name === 'active') {
      setPlanForm(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setPlanForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const addFeature = () => {
    setPlanForm(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };
  
  const removeFeature = (index) => {
    setPlanForm(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };
  
  const handleFeatureChange = (index, value) => {
    setPlanForm(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };
  
  const handleSavePlan = async () => {
    try {
      setSavingPlan(true);
      
      // Validate form
      if (!planForm.name.trim()) {
        setError('Plan name is required');
        setSavingPlan(false);
        return;
      }
      
      if (!planForm.price || isNaN(parseFloat(planForm.price))) {
        setError('Valid price is required');
        setSavingPlan(false);
        return;
      }
      
      // Filter out empty features
      const filteredFeatures = planForm.features.filter(f => f.trim());
      if (filteredFeatures.length === 0) {
        setError('At least one feature is required');
        setSavingPlan(false);
        return;
      }
      
      let response;
      if (editMode && currentPlan) {
        // Update existing plan
        response = await stripeService.updatePlan(
          currentPlan.id,
          {
            ...planForm,
            features: filteredFeatures
          }
        );
        
        setSuccess('Plan updated successfully');
      } else {
        // Create new plan
        response = await stripeService.createPlan({
          ...planForm,
          features: filteredFeatures
        });
        
        setSuccess('Plan created successfully');
      }
      
      // Refresh plans list
      const refreshedPlans = await stripeService.getPlatformPlans();
      if (refreshedPlans && refreshedPlans.success && Array.isArray(refreshedPlans.data)) {
        setPlans(refreshedPlans.data);
      }
      
      // Close dialog
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving plan:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} plan: ${err.message}`);
    } finally {
      setSavingPlan(false);
    }
  };
  
  const handlePlanMenuOpen = (event, planId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlanId(planId);
  };
  
  const handlePlanMenuClose = () => {
    setAnchorEl(null);
    setSelectedPlanId(null);
  };
  
  const getSelectedPlan = () => {
    return plans.find(plan => plan.id === selectedPlanId);
  };
  
  const handleDeleteConfirm = (plan) => {
    setCurrentPlan(plan);
    setDeleteConfirmOpen(true);
  };
  
  const handleDeletePlan = async () => {
    if (!currentPlan) return;
    
    try {
      setLoading(true);
      await stripeService.deletePlan(currentPlan.id);
      
      // Remove from UI
      setPlans(plans.filter(p => p.id !== currentPlan.id));
      
      setSuccess(`Plan "${currentPlan.name}" deleted successfully`);
      setDeleteConfirmOpen(false);
    } catch (err) {
      console.error('Error deleting plan:', err);
      setError(`Failed to delete plan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSetRecommended = async (planId) => {
    if (!planId) return;
    
    try {
      setLoading(true);
      handlePlanMenuClose();
      
      await stripeService.setRecommendedPlan(planId);
      
      // Update local state
      setPlans(plans.map(plan => ({
        ...plan,
        recommended: plan.id === planId
      })));
      
      setSuccess('Recommended plan updated successfully');
    } catch (err) {
      console.error('Error setting recommended plan:', err);
      setError(`Failed to update recommended plan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && plans.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f5f7fa',
        }}
      >
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <AssessmentIcon sx={{ mr: 1 }} /> Subscription Plans
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Manage your platform's subscription plans and pricing
          </Typography>
        </Paper>

        {/* Action Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Available Plans</Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={handleRefreshPlans}
              sx={{ mr: 2 }}
            >
              Refresh Plans
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
              }}
              disabled={isApiKeyError}
            >
              Add New Plan
            </Button>
          </Box>
        </Box>

        {/* Alert Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {isApiKeyError ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {error}
                </Typography>
                <Typography variant="body2">
                  Your Stripe integration is not working properly due to an invalid API key.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    How to fix this:
                  </Typography>
                  <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                    <li>Check your server's environment variables for the correct Stripe API key</li>
                    <li>Ensure you're using a valid secret key (starts with "sk_")</li>
                    <li>Verify you're using the right key for your environment (test or live)</li>
                    <li>Once fixed, restart your server and refresh this page</li>
                  </ul>
                </Box>
              </>
            ) : (
              error
            )}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Loading overlay */}
        {loading && plans.length > 0 && (
          <Box sx={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999
          }}>
            <CircularProgress />
          </Box>
        )}

        {/* API Key Error Message */}
        {isApiKeyError && plans.length === 0 && !loading && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              border: '1px dashed #d32f2f',
              bgcolor: '#ffebee',
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" color="error" gutterBottom>
              Stripe API Key Error
            </Typography>
            <Typography variant="body1" paragraph>
              We couldn't connect to Stripe due to an invalid API key. Subscription plan management requires a valid Stripe configuration.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Please check your server's environment variables and ensure the correct Stripe secret key is configured.
            </Typography>
          </Paper>
        )}

        {/* No plans message */}
        {plans.length === 0 && !loading && !isApiKeyError && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No plans found. Create your first subscription plan using the "Add New Plan" button.
          </Alert>
        )}

        {/* Plans Grid */}
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card 
                elevation={plan.recommended ? 3 : 1} 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  },
                  border: plan.recommended ? '2px solid #3f51b5' : 'none',
                  opacity: plan.active ? 1 : 0.7
                }}
              >
                {plan.recommended && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      zIndex: 1
                    }}
                  >
                    <Chip
                      label="Recommended"
                      color="primary"
                      size="small"
                      icon={<StarFilledIcon />}
                    />
                  </Box>
                )}

                {/* Plan Actions Menu */}
                <IconButton
                  aria-label="more"
                  aria-controls="plan-menu"
                  aria-haspopup="true"
                  onClick={(e) => handlePlanMenuOpen(e, plan.id)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <MoreVertIcon />
                </IconButton>

                <CardContent sx={{ pt: plan.recommended ? 4 : 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {plan.name}
                    {!plan.active && (
                      <Chip size="small" label="Inactive" color="default" sx={{ ml: 1, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  
                  <Typography 
                    variant="h4" 
                    component="div" 
                    color="primary"
                    sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}
                  >
                    {formatCurrency(plan.price)}
                    <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      /{plan.interval}
                    </Typography>
                  </Typography>

                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ mb: 3, minHeight: 60 }}
                  >
                    {plan.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Features:
                  </Typography>
                  
                  <List dense disablePadding>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} disableGutters sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
                
                <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                  <Chip
                    label={`${plan.tenantCount} ${plan.tenantCount === 1 ? 'tenant' : 'tenants'}`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                  
                  <Box>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={() => handleOpenDialog(plan)}
                      title="Edit plan"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteConfirm(plan)}
                      title="Delete plan"
                      disabled={plan.tenantCount > 0}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Plan Form Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editMode ? `Edit ${currentPlan?.name} Plan` : 'Create New Plan'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Plan Name"
                  name="name"
                  value={planForm.name}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                />
                
                <TextField
                  label="Description"
                  name="description"
                  value={planForm.description}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={3}
                  required
                />
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Price"
                    name="price"
                    value={planForm.price}
                    onChange={handleInputChange}
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  
                  <TextField
                    label="Billing Interval"
                    name="interval"
                    value={planForm.interval}
                    onChange={handleInputChange}
                    select
                    SelectProps={{ native: true }}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    required
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </TextField>
                </Box>
                
                <TextField
                  label="Plan ID"
                  name="planId"
                  value={planForm.planId}
                  onChange={handleInputChange}
                  select
                  SelectProps={{ native: true }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  required
                  helperText="Must match the plan IDs in the Tenant model"
                >
                  <option value="basic">Basic</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="custom">Custom</option>
                </TextField>
                
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={planForm.isRecommended}
                        onChange={handleInputChange}
                        name="isRecommended"
                        color="primary"
                      />
                    }
                    label="Mark as Recommended Plan"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={planForm.active}
                        onChange={handleInputChange}
                        name="active"
                        color="primary"
                      />
                    }
                    label="Plan is Active"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Plan Features</Typography>
                  <Button
                    startIcon={<AddCircleIcon />}
                    onClick={addFeature}
                    size="small"
                  >
                    Add Feature
                  </Button>
                </Box>
                
                {planForm.features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      gap: 1
                    }}
                  >
                    <CheckCircleIcon color="primary" fontSize="small" />
                    <TextField
                      value={feature}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder={`Feature ${index + 1}`}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => removeFeature(index)}
                      disabled={planForm.features.length <= 1}
                    >
                      <RemoveCircleIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCloseDialog} color="inherit" disabled={savingPlan}>
              Cancel
            </Button>
            <Button
              onClick={handleSavePlan}
              variant="contained"
              color="primary"
              startIcon={editMode ? <EditIcon /> : <AddIcon />}
              disabled={savingPlan}
            >
              {savingPlan ? (
                <CircularProgress size={24} />
              ) : (
                editMode ? 'Update Plan' : 'Create Plan'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title" sx={{ color: 'error.main' }}>
            Delete Plan Permanently?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Typography paragraph>
                <strong>Are you absolutely sure you want to delete the "{currentPlan?.name}" plan?</strong> This action cannot be undone and will remove this plan from your Stripe account.
              </Typography>
              <Typography paragraph>
                {currentPlan?.tenantCount > 0 ? (
                  <Box component="span" fontWeight="bold" color="error.main">
                    This plan has {currentPlan.tenantCount} active subscribers and cannot be deleted.
                  </Box>
                ) : (
                  "This plan has no active subscribers and can be safely deleted."
                )}
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleDeletePlan}
              color="error"
              variant="contained"
              autoFocus
              disabled={currentPlan?.tenantCount > 0 || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Delete Permanently'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Plan Actions Menu */}
        <Menu
          id="plan-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handlePlanMenuClose}
        >
          <MenuItem onClick={() => {
            handleOpenDialog(getSelectedPlan());
            handlePlanMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit Plan" />
          </MenuItem>
          <MenuItem onClick={() => handleSetRecommended(selectedPlanId)}>
            <ListItemIcon>
              <StarIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Set as Recommended" />
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteConfirm(getSelectedPlan());
            handlePlanMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Delete Plan" />
          </MenuItem>
        </Menu>
      </Box>
    </Container>
  );
};

export default SuperAdminPlanManagement;